import React from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Typography,
} from '@mui/material'
import { useAppSelector } from '@hooks/reduxHooks'
import { Search, Star } from 'lucide-react'

type Props = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onExamSelect: (examId: number) => Promise<void>
  onOpenFullSelector: () => void
  loadingExamId?: number | null
}

const PreferredExamsPopover: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  onExamSelect,
  onOpenFullSelector,
  loadingExamId,
}) => {
  const memberPreferences = useAppSelector(
    (state) => state.memberPreferences.preferences,
  )

  const handleExamClick = async (examId: number) => {
    await onExamSelect(examId)
    onClose()
  }

  const handleFullSelectorClick = () => {
    onOpenFullSelector()
    onClose()
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          mt: 0.5,
          width: 280,
          maxWidth: 'calc(100vw - 24px)',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 12px 28px rgba(17, 24, 39, 0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ bgcolor: '#fff' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.75,
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: 24,
              height: 24,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              bgcolor: '#f5f3ff',
              color: '#7c3aed',
            }}
          >
            <Star size={14} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
                color: '#111827',
              }}
            >
              Examens favoris
            </Typography>
          </Box>
        </Box>

        <Box sx={{ maxHeight: 240, overflowY: 'auto', py: 0.25 }}>
          {memberPreferences?.favoriteExams &&
          memberPreferences.favoriteExams.length > 0 ? (
            memberPreferences.favoriteExams.map((exam) => {
              const isLoading = loadingExamId === exam.id
              return (
                <Button
                  key={exam.id}
                  fullWidth
                  disabled={isLoading || Boolean(loadingExamId)}
                  onClick={() => handleExamClick(exam.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 0.75,
                    minHeight: 38,
                    px: 1,
                    py: 0.5,
                    borderRadius: 0,
                    color: '#111827',
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#f9fafb',
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.25,
                      }}
                    >
                      {exam.label}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.35,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                          bgcolor: '#f9fafb',
                          px: 0.5,
                          py: 0.1,
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#374151',
                          lineHeight: 1.35,
                        }}
                      >
                        {exam.code}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#6b7280',
                        }}
                      >
                        {exam.modality}
                      </Typography>
                    </Box>
                  </Box>
                  {isLoading && (
                    <CircularProgress
                      size={14}
                      sx={{ color: '#7c3aed', flexShrink: 0 }}
                    />
                  )}
                </Button>
              )
            })
          ) : (
            <Box sx={{ px: 1, py: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                Aucun examen préféré
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 11, color: '#6b7280' }}>
                Configurez vos examens préférés
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleFullSelectorClick}
            disabled={Boolean(loadingExamId)}
            startIcon={<Search size={15} />}
            sx={{
              height: 30,
              borderRadius: '6px',
              borderColor: '#ddd6fe',
              color: '#6d28d9',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#c4b5fd',
                bgcolor: '#f5f3ff',
              },
            }}
          >
            Liste complète
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default PreferredExamsPopover
